// The tenant picks one accent colour. Everything drawn with it must stay
// readable (WCAG AA, 4.5:1), whatever colour they pick: text on the accent
// switches between white and near-black, and the accent used as text or as a
// link on the widget background is darkened or lightened until it passes.

export const DEFAULT_ACCENT = "#0f2e24";
const WHITE = "#ffffff";
const INK = "#111418";

type Rgb = [number, number, number];

export function parseHex(hex: string | null | undefined): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex ?? "");
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const toHex = ([r, g, b]: Rgb) => `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;

function luminance([r, g, b]: Rgb): number {
  const c = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!;
}

export function contrast(a: string, b: string): number {
  const la = luminance(parseHex(a)!);
  const lb = luminance(parseHex(b)!);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Moves the colour towards black (amount < 0) or white (amount > 0), 0..1. */
function mix(hex: string, amount: number): string {
  const rgb = parseHex(hex)!;
  const target = amount < 0 ? 0 : 255;
  const t = Math.abs(amount);
  return toHex(rgb.map((v) => v + (target - v) * t) as Rgb);
}

/** The accent adjusted in small steps until it reads on `background`. */
export function readableOn(accent: string, background: string, ratio = 4.5): string {
  const darken = luminance(parseHex(background)!) > 0.5;
  let color = accent;
  for (let step = 0; step < 20 && contrast(color, background) < ratio; step += 1) {
    color = mix(accent, (darken ? -1 : 1) * 0.05 * (step + 1));
  }
  return color;
}

export interface AccentPalette {
  accent: string;
  onAccent: string;
  /** Accent as text or link colour on the widget background. */
  accentText: string;
  /** Pressed / hovered accent surface. */
  accentStrong: string;
}

export function accentPalette(input: string | null | undefined, background: string): AccentPalette {
  const chosen = parseHex(input) ? `#${input!.replace("#", "").toLowerCase()}` : DEFAULT_ACCENT;
  // Brands expect white on their colour: keep white whenever a slightly
  // deeper shade gets there (orange, mid greens); ink only on light accents.
  const onAccent = contrast(chosen, WHITE) >= 3 ? WHITE : contrast(chosen, WHITE) >= contrast(chosen, INK) ? WHITE : INK;
  // Mid tones (#777, saturated greens) reach neither text colour at 4.5:1;
  // the surface then shifts just enough, the label stays white or ink.
  const accent = readableOn(chosen, onAccent);
  // Pressing darkens, except for accents already near black, which lighten.
  const nearBlack = luminance(parseHex(accent)!) < 0.03;
  return {
    accent,
    onAccent,
    accentText: readableOn(accent, background),
    accentStrong: mix(accent, nearBlack ? 0.14 : -0.12),
  };
}
